import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import { Search, Plus, Trash2, Edit2, Check, X, Database, ArrowRight, BarChart3 } from 'lucide-react';
import QualityBadges, { buildAlertLookup, getBadgesForEntity } from '../components/intelligence/QualityBadges';

const VendorMappings = () => {
  const [mappings, setMappings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    item_alias: '',
    canonical_ingredient_id: '',
  });
  const [alertLookup, setAlertLookup] = useState({ byEntityId: {}, byIngredientName: {} });

  // Cache for vendor/ingredient lookups
  const vendorCache = useRef({});
  const ingredientCache = useRef({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [mappingsRes, vendorsRes, ingredientsRes, statsRes] = await Promise.all([
        api.getVendorMappings({ search, vendor_id: vendorFilter || undefined, limit: 100 }),
        api.getVendors(''),
        api.getCanonicalIngredients({ limit: 500 }),
        api.getVendorMappingStats(vendorFilter || undefined),
      ]);

      setMappings(mappingsRes.data.mappings || []);
      
      // Handle vendors - might be array or wrapped
      const vendorList = Array.isArray(vendorsRes.data) 
        ? vendorsRes.data 
        : (vendorsRes.data?.vendors || []);
      setVendors(vendorList);
      vendorList.forEach(v => { vendorCache.current[v.vendor_id] = v.vendor_name; });

      // Handle ingredients
      const ingredientList = Array.isArray(ingredientsRes.data)
        ? ingredientsRes.data
        : (ingredientsRes.data?.ingredients || []);
      setIngredients(ingredientList);
      ingredientList.forEach(i => { ingredientCache.current[i.ingredient_id] = i.ingredient_name; });

      setStats(statsRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [search, vendorFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load data quality alerts for inline badges
  useEffect(() => {
    api.getStructuredAlerts()
      .then((res) => setAlertLookup(buildAlertLookup(res.data?.alerts || [])))
      .catch((e) => console.error('Error loading quality alerts:', e));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMapping) {
        await api.updateVendorMapping(editingMapping.mapping_id, {
          canonical_ingredient_id: formData.canonical_ingredient_id,
        });
      } else {
        await api.createVendorMapping(formData);
      }
      setShowForm(false);
      setEditingMapping(null);
      setFormData({ vendor_id: '', item_alias: '', canonical_ingredient_id: '' });
      loadData();
    } catch (error) {
      console.error('Error saving mapping:', error);
      alert(error.response?.data?.detail || 'Failed to save mapping');
    }
  };

  const handleDelete = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;
    try {
      await api.deleteVendorMapping(mappingId);
      loadData();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      alert('Failed to delete mapping');
    }
  };

  const openEditForm = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      vendor_id: mapping.vendor_id,
      item_alias: mapping.item_alias,
      canonical_ingredient_id: mapping.canonical_ingredient_id,
    });
    setShowForm(true);
  };

  const getVendorName = (vendorId) => {
    return vendorCache.current[vendorId] || vendorId?.slice(0, 8) || 'Unknown';
  };

  const getIngredientName = (ingredientId) => {
    return ingredientCache.current[ingredientId] || ingredientId?.slice(0, 8) || 'Unknown';
  };

  return (
    <div className="p-8" data-testid="vendor-mappings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vendor Mapping Memory</h1>
          <p className="text-gray-600 mt-2">
            Teach the system to automatically map vendor items to canonical ingredients
          </p>
        </div>
        <button
          onClick={() => {
            setEditingMapping(null);
            setFormData({ vendor_id: '', item_alias: '', canonical_ingredient_id: '' });
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Add Mapping
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Mappings</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_mappings}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Vendors Mapped</p>
                <p className="text-2xl font-bold text-gray-900">{stats.vendor_breakdown?.length || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ArrowRight className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Auto-Applied</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.top_mappings?.reduce((sum, m) => sum + (m.usage_count || 0), 0) || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search item aliases or ingredients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="w-full md:w-64">
            <select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Vendors</option>
              {vendors.map((vendor) => (
                <option key={vendor.vendor_id} value={vendor.vendor_id}>
                  {vendor.vendor_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Mappings Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading mappings...</div>
        ) : mappings.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No mappings found.</p>
            <p className="text-sm mt-2">Create mappings to teach the system how to automatically map vendor items.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Alias (Invoice Name)
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  →
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Canonical Ingredient
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confidence
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mappings.map((mapping) => (
                <tr key={mapping.mapping_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {mapping.vendor_name || getVendorName(mapping.vendor_id)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">
                      {mapping.item_alias}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <ArrowRight className="w-4 h-4 text-gray-400 mx-auto" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-blue-600 font-medium">
                      {mapping.canonical_ingredient_name || getIngredientName(mapping.canonical_ingredient_id)}
                    </span>
                    <QualityBadges
                      badges={getBadgesForEntity(
                        alertLookup,
                        mapping.canonical_ingredient_id,
                        mapping.canonical_ingredient_name || getIngredientName(mapping.canonical_ingredient_id)
                      )}
                      className="mt-0.5"
                    />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {mapping.usage_count || 0}×
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      mapping.confidence >= 0.9 ? 'bg-green-100 text-green-800' :
                      mapping.confidence >= 0.7 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {Math.round((mapping.confidence || 1) * 100)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEditForm(mapping)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(mapping.mapping_id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingMapping ? 'Edit Mapping' : 'Create New Mapping'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor *
                </label>
                <select
                  required
                  value={formData.vendor_id}
                  onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
                  disabled={!!editingMapping}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select Vendor</option>
                  {vendors.map((vendor) => (
                    <option key={vendor.vendor_id} value={vendor.vendor_id}>
                      {vendor.vendor_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Alias (as it appears on invoices) *
                </label>
                <input
                  type="text"
                  required
                  value={formData.item_alias}
                  onChange={(e) => setFormData({ ...formData, item_alias: e.target.value })}
                  disabled={!!editingMapping}
                  placeholder="e.g., TOMATO FRESH LOCAL"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the exact item name as it appears on vendor invoices
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maps To Ingredient *
                </label>
                <select
                  required
                  value={formData.canonical_ingredient_id}
                  onChange={(e) => setFormData({ ...formData, canonical_ingredient_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Canonical Ingredient</option>
                  {ingredients.map((ing) => (
                    <option key={ing.ingredient_id} value={ing.ingredient_id}>
                      {ing.ingredient_name} {ing.ingredient_category ? `(${ing.ingredient_category})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingMapping(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Check size={18} />
                  {editingMapping ? 'Update' : 'Create'} Mapping
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorMappings;
